import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { Logo, PageContainer } from "@/components/Logo";
import { MarketingFooter } from "@/components/MarketingShell";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/privacy-policy")({
  component: PrivacyPolicyPage,
  head: () => ({
    meta: [
      { title: "Privacy Policy — MissionReady" },
      { name: "description", content: "How MissionReady collects, uses, stores, and protects personal and operational data across our field operations platform." },
      ...socialMeta({
        title: "Privacy Policy — MissionReady",
        description: "How MissionReady collects, uses, stores, and protects personal and operational data.",
        url: "https://missionready.dev/privacy-policy",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/privacy-policy" }],
    scripts: [...ORG_WEBSITE_JSONLD],
  }),
});

const LAST_UPDATED = "September 8, 2026";

function PrivacyPolicyPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur">
        <PageContainer className="flex h-16 items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm"><Link to="/login">Sign in</Link></Button>
            <Button asChild size="sm"><Link to="/signup">Start free trial</Link></Button>
          </div>
        </PageContainer>
      </header>

      <main>
        <PageContainer className="max-w-3xl py-16 md:py-24">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Legal</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight md:text-5xl">Privacy Policy</h1>
          <p className="mt-3 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

          <div className="prose-legal mt-10 space-y-8 text-[15px] leading-relaxed text-foreground/90">
            <section>
              <p>
                MissionReady ("MissionReady", "we", "us", or "our") provides a multi-tenant
                field operations and patrol accountability platform (the "Service"). This
                Privacy Policy explains what information we collect, how we use it, and the
                choices you have. It applies to our website, web application, and mobile
                clients.
              </p>
            </section>

            <Section title="1. Who is the controller">
              <p>
                If you signed up for a MissionReady workspace, your employer or organization
                ("Customer") is the data controller for personal data processed inside that
                workspace, and we act as their processor. For our marketing site and account
                signup, MissionReady is the controller.
              </p>
            </Section>

            <Section title="2. Information we collect">
              <ul className="list-disc space-y-2 pl-6">
                <li><strong>Account data:</strong> name, email, password hash, workspace name, role, and billing contact.</li>
                <li><strong>Operational data you submit:</strong> sites, post orders, incidents, guard tour runs, checkpoint scans, attachments, shift reports, messages, and dispatch records.</li>
                <li><strong>Location data:</strong> coordinates, an accuracy figure, the capture time, and whether the reading was current or last known — recorded only at the moment of a specific work action (see Section 9), or continuously during a live patrol that your organization enables and the officer starts.</li>
                <li><strong>Voice recordings & transcripts:</strong> audio you record for a Daily Activity Report or the voice assistant, plus the resulting transcript, speaker labels, timestamps, and summary (see Section 10).</li>
                <li><strong>Usage data:</strong> log entries, IP address, browser, OS, referrer, performance metrics, and feature events.</li>
                <li><strong>Cookies & similar:</strong> essential session cookies, security tokens, and limited analytics — see Section 8.</li>
                <li><strong>Payment data:</strong> handled by our payment processor; we store only the subscription state and a billing reference.</li>
              </ul>
            </Section>

            <Section title="3. How we use information">
              <ul className="list-disc space-y-2 pl-6">
                <li>Provide, operate, secure, and improve the Service.</li>
                <li>Authenticate users, enforce workspace isolation, and prevent abuse.</li>
                <li>Generate patrol replays, analytics, anomaly detection, and client-ready reports.</li>
                <li>Send transactional emails (invites, alerts, password resets, billing).</li>
                <li>Provide customer support and respond to your requests.</li>
                <li>Comply with legal obligations and enforce our Terms.</li>
              </ul>
            </Section>

            <Section title="4. Legal bases (EEA / UK)">
              <p>
                Where applicable, we rely on: (a) performance of a contract; (b) our
                legitimate interests in operating and securing the Service; (c) consent for
                optional features (e.g. marketing emails); and (d) compliance with legal
                obligations.
              </p>
            </Section>

            <Section title="5. Sharing & sub-processors">
              <p>We share personal data only with:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>Sub-processors who host, secure, or operate the Service (cloud hosting, database, email delivery, error monitoring, payment processing).</li>
                <li>Other members of your workspace, according to their assigned role.</li>
                <li>Authorities, when required by law or to protect rights, safety, and property.</li>
                <li>An acquirer in the event of a merger, acquisition, or asset sale, subject to this Policy.</li>
              </ul>
              <p>We do not sell personal data.</p>
            </Section>

            <Section title="6. International transfers">
              <p>
                Personal data may be processed in countries other than where you live. Where
                required, we rely on appropriate safeguards such as Standard Contractual
                Clauses.
              </p>
            </Section>

            <Section title="7. Retention">
              <p>
                We retain personal and operational data for as long as the workspace is
                active and as needed to provide the Service. Patrol replay, GPS, and
                heatmap data are retained according to your subscription plan. After
                workspace closure, data is deleted or anonymized within a reasonable period,
                except where retention is required by law.
              </p>
            </Section>

            <Section title="8. Cookies">
              <p>
                We use strictly necessary cookies for authentication and security. With your
                consent, we also use analytics cookies (Google Analytics) to understand product
                usage and preference cookies to remember UI choices. You can review and change
                your choices any time using the{" "}
                <button
                  type="button"
                  onClick={() => { void import("@/lib/cookie-consent").then((m) => m.openCookiePreferences()); }}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Cookie preferences
                </button>{" "}
                center (also linked in the footer). Disabling strictly necessary cookies will
                break sign-in.
              </p>
            </Section>


            <Section title="9. Location & GPS">
              <p>
                Location is recorded only at the moment a signed-in user takes one of these
                actions: submitting a daily activity entry, filing an incident or field
                report, scanning a checkpoint or equipment code, clocking in or out, or
                activating SOS / Man Down. We do not follow anyone in the background — nothing
                is captured while the app is closed, off shift, or simply being browsed.
              </p>
              <p>
                Continuous live patrol tracking is a separate feature that an organization
                enables and that the officer starts themselves.
              </p>
              <p>
                Before any capture, users are shown a versioned Location & GPS disclosure and
                must acknowledge it; the acknowledgment is stored with the exact version and
                wording shown. Acknowledgment can be withdrawn at any time under Settings →
                Location privacy, which stops future capture but does not alter records already
                filed. Device-level location permission remains under the user's control.
              </p>
              <p>
                Location attached to a report, incident, SOS alert, or checkpoint scan is part
                of that record and kept as long as the record. Standalone location captures
                that are not part of a record are deleted automatically after 90 days. If a
                reading was unavailable or declined, that fact is stored instead of a position
                — never a guessed one.
              </p>
            </Section>

            <Section title="10. Voice recordings, transcription & AI">
              <p>
                Voice Daily Activity Reports and the in-app assistant record audio only while a
                user is actively recording. Audio is sent to our speech provider, Deepgram, to
                produce a transcript with speaker labels and timestamps; AI features may also
                generate summaries. Recordings and transcripts belong to the workspace that
                created them and are visible only to authorized members of that workspace.
              </p>
              <p>
                Original recordings and transcripts are preserved as source records for the
                reports built from them and are retained under your organization's retention
                settings. We do not use your recordings or operational content to train
                general-purpose AI models.
              </p>
            </Section>

            <Section title="11. Security">
              <p>
                We use encryption in transit, encryption at rest for managed databases,
                row-level security for workspace isolation, signed checkpoint tokens, audit
                logging, and least-privilege access controls. No system is perfectly secure;
                you are responsible for protecting your account credentials.
              </p>
            </Section>

            <Section title="12. Your rights">
              <p>
                Depending on your jurisdiction, you may have the right to access, correct,
                delete, restrict, or port your personal data, and to object to certain
                processing. If your data is held inside a Customer workspace, please direct
                your request to that Customer; we will assist them as their processor.
                Otherwise, contact us using the details below.
              </p>
            </Section>

            <Section title="13. Children">
              <p>
                The Service is not directed to children under 16, and we do not knowingly
                collect personal data from them.
              </p>
            </Section>

            <Section title="14. Changes">
              <p>
                We may update this Policy from time to time. Material changes will be
                announced in-product or by email. Continued use of the Service after the
                effective date constitutes acceptance.
              </p>
            </Section>

            <Section title="15. Contact">
              <p>
                Questions about this Policy or our data practices? Email{" "}
                <a className="text-primary hover:underline" href="mailto:privacy@missionready.dev">privacy@missionready.dev</a>.
              </p>
            </Section>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-border pt-8 text-sm">
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            <span className="text-muted-foreground">·</span>
            <Link to="/" className="text-muted-foreground hover:text-foreground">Back to home</Link>
          </div>
        </PageContainer>
      </main>

      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-muted-foreground">{children}</div>
    </section>
  );
}
